// src/StripViewer.tsx
import React, { useState, useEffect, useRef } from 'react';
import { ResolutionLevelDto, ViewerInitializationDto, UnifiedStripElement } from './image-strip-viewer-types';
import getConfig from '../../app/config';
import { scaleLinear } from 'd3';

const API_BASE_URL = getConfig().apiUrl;
const CSS_RENDER_OVERLAP_PX = 1;

interface StripViewerProps {
    initialStartDepthMeters: number;
    initialEndDepthMeters: number;
    clientId: number;
    projectId: number;
    boreholeId: number;
    imageTypeId: number;
    authToken: string;
    viewerDisplayHeightPx: number;
    viewerWidth: number;
    isPanning: boolean;
    onMouseMove?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onMouseOut?: () => void;
}

const StripViewer: React.FC<StripViewerProps> = ({
    initialStartDepthMeters,
    initialEndDepthMeters,
    clientId,
    projectId,
    boreholeId,
    imageTypeId,
    authToken,
    viewerDisplayHeightPx,
    viewerWidth,
    isPanning,
    onMouseMove,
    onMouseOut
}) => {
    const [initData, setInitData] = useState<ViewerInitializationDto | null>(null);
    const [viewStartDepth, setViewStartDepth] = useState<number>(initialStartDepthMeters);
    const [viewEndDepth, setViewEndDepth] = useState<number>(initialEndDepthMeters);
    const [displayElements, setDisplayElements] = useState<UnifiedStripElement[]>([]);
    const [backgroundElements, setBackgroundElements] = useState<UnifiedStripElement[]>([]);
    const [overviewElements, setOverviewElements] = useState<UnifiedStripElement[]>([]);

    const animationRef = useRef<number>();
    const loadedKeysRef = useRef<Set<string>>(new Set<string>());
    let previousElementRoundedBottomPx: number | null = null;

    useEffect(() => {
        const fetchInit = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/${imageTypeId}/stripOverview?access_token=${authToken}`);
                if (!response.ok) throw new Error(`Init Network: ${response.status}`);
                const data: ViewerInitializationDto = await response.json();
                if (data.resolutionLevels) {
                    data.resolutionLevels.sort((a, b) => a.levelIndex - b.levelIndex);
                }
                setInitData(data);

                if (data.resolutionLevels && data.resolutionLevels.length > 0) {                    
                    const usePdfImages = data.usePdfImages;
                    const overviewLevel = data.resolutionLevels[0];
                    const overviewStrips: UnifiedStripElement[] = [];
                    for (let i = 0; i < overviewLevel.totalStripsInLevel; i++) {
                        const tileActualStartDepthM = data.minDatasetDepth + (i * overviewLevel.depthPerStripMeters);
                        overviewStrips.push({
                            key: `res${overviewLevel.levelIndex}-s${i}`,
                            src: `${API_BASE_URL}client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/${imageTypeId}/strip/${overviewLevel.levelIndex}/${i}.png?usePdfImages=${usePdfImages}&access_token=${authToken}`,
                            containerTopPx: 0, // Will be calculated dynamically
                            containerHeightPx: 0, // Will be calculated dynamically
                            imageElementScreenHeightPx: 0, // Will be calculated dynamically
                            imageElementOffsetYPx: 0, // Will be calculated dynamically
                            screenWidthPx: viewerWidth,
                            resolutionLevelIndex: overviewLevel.levelIndex,
                            stripIndexInLevel: i,
                            tileNativeResolutionPxPerM: overviewLevel.pixelsPerMeter,
                            tileActualStartDepthM: tileActualStartDepthM,
                            tileActualEndDepthM: tileActualStartDepthM + overviewLevel.depthPerStripMeters,
                            loaded: false,
                            errored: false,
                            fullyVisible: false
                        });
                    }
                    setOverviewElements(overviewStrips);
                }

                let startDepth = initialStartDepthMeters;
                let endDepth = initialEndDepthMeters;
                if (data.totalDatasetSpan > 0.001 && ((startDepth === 0 && endDepth === 0) || (endDepth - startDepth < 0.001))) {
                    startDepth = data.minDatasetDepth;
                    endDepth = data.maxDatasetDepth;
                }
                setViewStartDepth(startDepth);
                setViewEndDepth(endDepth);
            } catch (error) {
                console.error("Failed to fetch initialization data:", error);
                setViewStartDepth(initialStartDepthMeters);
                setViewEndDepth(initialEndDepthMeters);
            }
        };
        fetchInit();
    }, [clientId, projectId, boreholeId, imageTypeId, authToken, viewerWidth]);

    useEffect(() => {
        if (!initData || isPanning) {
            setViewStartDepth(initialStartDepthMeters);
            setViewEndDepth(initialEndDepthMeters);
            return;
        }

        const tStart = performance.now();
        const duration = 200;
        const startDepthStart = viewStartDepth;
        const endDepthStart = viewEndDepth;
        const startDepthDiff = initialStartDepthMeters - startDepthStart;
        const endDepthDiff = initialEndDepthMeters - endDepthStart;

        if (Math.abs(startDepthDiff) < 0.001 && Math.abs(endDepthDiff) < 0.001) return;

        const animate = (t: number) => {
            const elapsed = t - tStart;
            const progress = Math.min(elapsed / duration, 1);
            setViewStartDepth(startDepthStart + startDepthDiff * progress);
            setViewEndDepth(endDepthStart + endDepthDiff * progress);
            if (progress < 1) animationRef.current = requestAnimationFrame(animate);
        };

        if (animationRef.current) cancelAnimationFrame(animationRef.current);
        animationRef.current = requestAnimationFrame(animate);
        return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
    }, [initialStartDepthMeters, initialEndDepthMeters, initData, isPanning]);

    useEffect(() => {
        if (!initData?.resolutionLevels?.length || initData.totalDatasetSpan <= 0) return;

        const oldElementsMap = new Map(displayElements.map(el => [el.key, el]));
        const visibleDepthRange = viewEndDepth - viewStartDepth;
        if (visibleDepthRange <= 0) return;

        const currentPixelsPerMeterOnScreen = viewerDisplayHeightPx / visibleDepthRange;
        let chosenResLevel = initData.resolutionLevels[0];
        for (const level of initData.resolutionLevels) {
            if (level.pixelsPerMeter >= currentPixelsPerMeterOnScreen) {
                chosenResLevel = level;
                break;
            }
            chosenResLevel = level;
        }

        const tileDepthCoverageMeters = chosenResLevel.depthPerStripMeters;
        if (tileDepthCoverageMeters <= 0) return;

        const firstVisibleStripIdx = Math.max(0, Math.floor((viewStartDepth - initData.minDatasetDepth) / tileDepthCoverageMeters));
        const lastVisibleStripIdx = Math.min(chosenResLevel.totalStripsInLevel - 1, Math.ceil((viewEndDepth - initData.minDatasetDepth) / tileDepthCoverageMeters) - 1);
        const usePdfImages = initData.usePdfImages;
        const newDisplayElements: UnifiedStripElement[] = [];
        for (let stripIdx = firstVisibleStripIdx; stripIdx <= lastVisibleStripIdx; stripIdx++) {
            const tileActualStartDepthM = initData.minDatasetDepth + (stripIdx * tileDepthCoverageMeters);
            const tileKey = `res${chosenResLevel.levelIndex}-s${stripIdx}`;
            const prevEl = oldElementsMap.get(tileKey);
            newDisplayElements.push({
                key: tileKey,
                src: `${API_BASE_URL}client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/${imageTypeId}/strip/${chosenResLevel.levelIndex}/${stripIdx}.png?usePdfImages=${usePdfImages}&access_token=${authToken}`,
                containerTopPx: 0, // Will be calculated dynamically
                containerHeightPx: 0, // Will be calculated dynamically
                imageElementScreenHeightPx: 0, // Will be calculated dynamically
                imageElementOffsetYPx: 0, // Will be calculated dynamically
                screenWidthPx: viewerWidth,
                resolutionLevelIndex: chosenResLevel.levelIndex,
                stripIndexInLevel: stripIdx,
                tileNativeResolutionPxPerM: chosenResLevel.pixelsPerMeter,
                tileActualStartDepthM: tileActualStartDepthM,
                tileActualEndDepthM: tileActualStartDepthM + tileDepthCoverageMeters,
                loaded: loadedKeysRef.current.has(tileKey) || prevEl?.loaded || false,
                errored: prevEl?.errored || false,
                fullyVisible: prevEl?.fullyVisible || false
            });
        }

        if (displayElements.length > 0 && displayElements[0]?.resolutionLevelIndex !== newDisplayElements[0]?.resolutionLevelIndex) {
            setBackgroundElements(prev => {
                // Only add the previous foreground elements to the background cache
                const combined = [...prev, ...displayElements];
                const highestResMap = new Map<string, UnifiedStripElement>();
                combined.forEach(el => {
                    const rangeKey = `${el.tileActualStartDepthM.toFixed(5)}-${el.tileActualEndDepthM.toFixed(5)}`;
                    const existing = highestResMap.get(rangeKey);
                    if (!existing || (el.tileNativeResolutionPxPerM ?? 0) > (existing.tileNativeResolutionPxPerM ?? 0)) {
                        highestResMap.set(rangeKey, el);
                    }
                });
                return Array.from(highestResMap.values());
            });
        }
        setDisplayElements(newDisplayElements);
    }, [initData, viewStartDepth, viewEndDepth, viewerWidth, clientId, projectId, boreholeId, imageTypeId, authToken, viewerDisplayHeightPx]);

    const handleImageLoad = (key: string, layer: 'overview' | 'background' | 'foreground') => {
        loadedKeysRef.current.add(key);
        const updater = (elements: UnifiedStripElement[]) => elements.map(el => el.key === key ? { ...el, loaded: true } : el);
        if (layer === 'overview') {
            setOverviewElements(updater);
        } else if (layer === 'background') {
            setBackgroundElements(updater);
        } else {
            setDisplayElements(updater);
        }
    };

    const handleImageError = (key: string, layer: 'overview' | 'background' | 'foreground') => {
        // On error, mark as 'errored' but also 'loaded' to make its white container visible.
        const updater = (elements: UnifiedStripElement[]) => elements.map(el => el.key === key ? { ...el, errored: true, loaded: true } : el);
        if (layer === 'overview') {
            setOverviewElements(updater);
        } else if (layer === 'background') {
            setBackgroundElements(updater);
        } else {
            setDisplayElements(updater);
        }
    };

    const handleImageFadeInComplete = (key: string, layer: 'overview' | 'background' | 'foreground') => {
        // We only need to track this for filtering purposes, which applies to foreground tiles.
        if (layer !== 'foreground') return;

        const updater = (elements: UnifiedStripElement[]) =>
            elements.map(el => (el.key === key ? { ...el, fullyVisible: true } : el));
        
        setDisplayElements(updater);
    };

    const renderElements = (elements: UnifiedStripElement[], layer: 'foreground' | 'background' | 'overview', applyStitching: boolean) => {
        const visibleDepthRange = viewEndDepth - viewStartDepth;
        if (visibleDepthRange <= 0) return null;
        const currentPixelsPerMeterOnScreen = viewerDisplayHeightPx / visibleDepthRange;

        return elements.map((element) => {
            const visiblePortionOfTileStartDepth = Math.max(element.tileActualStartDepthM, viewStartDepth);
            const visiblePortionOfTileEndDepth = Math.min(element.tileActualEndDepthM, viewEndDepth);
            const visiblePortionDepthSpanM = visiblePortionOfTileEndDepth - visiblePortionOfTileStartDepth;

            if (visiblePortionDepthSpanM <= 0) return null;

            let containerTopPx_precise = (visiblePortionOfTileStartDepth - viewStartDepth) * currentPixelsPerMeterOnScreen;
            const containerHeightPx_precise = visiblePortionDepthSpanM * currentPixelsPerMeterOnScreen;

            if (containerHeightPx_precise < 0.5) return null;

            if (applyStitching && previousElementRoundedBottomPx !== null) {
                containerTopPx_precise = previousElementRoundedBottomPx - CSS_RENDER_OVERLAP_PX;
            }

            const roundedContainerTopPx = Math.round(containerTopPx_precise);
            const roundedContainerHeightPx = Math.round(containerHeightPx_precise);

            if (applyStitching) {
                previousElementRoundedBottomPx = roundedContainerTopPx + roundedContainerHeightPx;
            }

            const tileDepthCoverageMeters = element.tileActualEndDepthM - element.tileActualStartDepthM;
            const imageElementScreenHeightPx = tileDepthCoverageMeters * currentPixelsPerMeterOnScreen;
            let imageElementOffsetYPx = 0;
            if (element.tileActualStartDepthM < visiblePortionOfTileStartDepth) {
                imageElementOffsetYPx = (visiblePortionOfTileStartDepth - element.tileActualStartDepthM) * currentPixelsPerMeterOnScreen;
            }

            const imageRenderingStyle: React.CSSProperties['imageRendering'] =
                (element.tileNativeResolutionPxPerM && currentPixelsPerMeterOnScreen > element.tileNativeResolutionPxPerM * 1.1)
                    ? 'pixelated' : 'auto';

            const zIndex = layer === 'overview' ? 1 : layer === 'background' ? 2 : 3;

            return (
                <div key={element.key} style={{
                    position: 'absolute',
                    top: `${roundedContainerTopPx}px`,
                    left: 0,
                    width: `${element.screenWidthPx - 2}px`,
                    height: `${roundedContainerHeightPx}px`,
                    overflow: 'hidden',
                    zIndex: zIndex,
                    backgroundColor: 'white',
                    opacity: element.loaded ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                }}
                onTransitionEnd={() => handleImageFadeInComplete(element.key, layer)}
                >
                    {!element.errored && (
                        <img
                            src={element.src}
                            alt={`Tile L${element.resolutionLevelIndex}-S${element.stripIndexInLevel}`}
                            style={{
                                position: 'absolute',
                                top: `${-Math.round(imageElementOffsetYPx)}px`,
                                left: 0,
                                width: '100%',
                                height: `${Math.round(imageElementScreenHeightPx)}px`,
                                objectFit: 'fill',
                                pointerEvents: 'none',
                                imageRendering: imageRenderingStyle,
                            }}
                            loading="eager"
                            ref={(imgEl) => {
                                if (!element.loaded && imgEl?.complete && imgEl?.naturalHeight > 0) {
                                    handleImageLoad(element.key, layer);
                                }
                            }}
                            onLoad={() => handleImageLoad(element.key, layer)}
                            onError={() => handleImageError(element.key, layer)}
                        />
                    )}
                </div>
            );
        });
    };

    // Filter out lower-resolution tiles that are fully covered by fully visible higher-resolution tiles.
    const fullyVisibleForegroundIntervals = displayElements
        .filter(el => el.fullyVisible)
        .map(el => ({ start: el.tileActualStartDepthM, end: el.tileActualEndDepthM }));

    const filteredBackgroundElements = backgroundElements.filter(bgEl => {
        const isCovered = fullyVisibleForegroundIntervals.some(fgInterval =>
            fgInterval.start <= bgEl.tileActualStartDepthM && fgInterval.end >= bgEl.tileActualEndDepthM
        );
        return !isCovered;
    });

    const loadedHigherPriorityIntervals = [
        ...fullyVisibleForegroundIntervals, // Use the new list here
        ...filteredBackgroundElements.filter(el => el.loaded).map(el => ({ start: el.tileActualStartDepthM, end: el.tileActualEndDepthM }))
    ];

    const filteredOverviewElements = overviewElements.filter(ovEl => {
        const isCovered = loadedHigherPriorityIntervals.some(hpInterval =>
            hpInterval.start <= ovEl.tileActualStartDepthM && hpInterval.end >= ovEl.tileActualEndDepthM
        );
        return !isCovered;
    });

    previousElementRoundedBottomPx = null;
    const overviewElementsToRender = renderElements(filteredOverviewElements, 'overview', true);
    
    previousElementRoundedBottomPx = null;
    const backgroundElementsToRender = renderElements(filteredBackgroundElements, 'background', false);
    
    previousElementRoundedBottomPx = null;
    const foregroundElementsToRender = renderElements(displayElements, 'foreground', true);

    const openInNewTab = (url: string) => {
        window.open(url, "_blank", "noreferrer");
      };

    return (
        <div style={{
            height: viewerDisplayHeightPx,
            border: '1px solid #ccc',
            overflow: 'hidden',
            position: 'relative',
            userSelect: 'none',
        }}
        onMouseMove={onMouseMove}
        onMouseOut={onMouseOut}
        >
            {overviewElementsToRender}
            {backgroundElementsToRender}
            {foregroundElementsToRender}
        </div>
    );
};

export default StripViewer;